import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Star, Quote, User, Building, Calendar, MessageSquare, CheckCircle } from 'lucide-react';
import { mockStats } from '../mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Reviews = ({ showAllReviews = false, maxReviews = 6 }) => {
  const [reviewForm, setReviewForm] = useState({
    name: '',
    company: '',
    rating: 5,
    text: ''
  });
  const [testimonials, setTestimonials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [showAll, setShowAll] = useState(showAllReviews);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Fetch testimonials from API
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/reviews`);
        setTestimonials(response.data);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
        // Keep empty array if API fails
        setTestimonials([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setReviewForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRatingChange = (rating) => {
    setReviewForm(prev => ({
      ...prev,
      rating
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/reviews`, {
        name: reviewForm.name,
        company: reviewForm.company,
        rating: reviewForm.rating,
        text: reviewForm.text
      });
      
      if (response.data.success) {
        setIsSubmitted(true);
        setSubmitMessage(response.data.message);
        setReviewForm({ name: '', company: '', rating: 5, text: '' });
        
        // Reset success message after 4 seconds
        setTimeout(() => {
          setIsSubmitted(false);
          setSubmitMessage('');
        }, 4000);
      } else {
        throw new Error(response.data.message || 'Failed to submit review');
      }
      
    } catch (error) {
      console.error('Error submitting review:', error);
      setSubmitMessage('Failed to submit review. Please try again or call us directly.');
      
      // Reset error message after 4 seconds
      setTimeout(() => {
        setSubmitMessage('');
      }, 4000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = (rating, interactive = false, size = 'h-5 w-5') => {
    return (
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${size} ${
              star <= rating
                ? 'text-yellow-400 fill-yellow-400'
                : 'text-gray-300'
            } ${interactive ? 'cursor-pointer hover:scale-110 transition-transform duration-200' : ''}`}
            onClick={interactive ? () => handleRatingChange(star) : undefined}
          />
        ))}
      </div>
    );
  };

  return (
    <section id="reviews" className="py-20 bg-gradient-to-br from-yellow-50 via-white to-orange-50 relative">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(251,191,36,0.1),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_70%,rgba(249,115,22,0.08),transparent_50%)]" />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-yellow-100 text-yellow-800 text-sm font-medium mb-6">
            <Star className="h-4 w-4 mr-2" />
            <span>Customer Reviews</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            What Our Customers Say
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Don't just take our word for it. See what businesses across the UK say about 
            our reliable same-day delivery service.
          </p>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {mockStats.map((stat, index) => {
            const IconComponent = {
              CheckCircle,
              Clock: Calendar,
              Users: Building,
              Phone: MessageSquare
            }[stat.icon] || CheckCircle;
            
            return (
              <Card key={index} className="text-center p-6 border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-white">
                <CardContent className="p-0">
                  <div className="flex justify-center mb-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
                      <IconComponent className="h-6 w-6 text-emerald-600" />
                    </div>
                  </div>
                  <div className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">{stat.number}</div>
                  <div className="text-sm text-slate-600 font-medium">{stat.label}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {isLoading ? (
            // Loading skeleton
            [...Array(6)].map((_, index) => (
              <Card key={index} className="relative p-6 border-0 shadow-lg bg-white animate-pulse">
                <CardContent className="p-0">
                  <div className="mb-4 flex space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="h-5 w-5 bg-gray-200 rounded"></div>
                    ))}
                  </div>
                  <div className="space-y-2 mb-6">
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
                    <div className="space-y-1">
                      <div className="h-4 bg-gray-200 rounded w-20"></div>
                      <div className="h-3 bg-gray-200 rounded w-16"></div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : testimonials.length > 0 ? (
            (showAll ? testimonials : testimonials.slice(0, maxReviews)).map((testimonial) => (
            <Card key={testimonial.id} className="relative p-6 border-0 shadow-lg hover:shadow-xl transition-all duration-500 transform hover:-translate-y-1 bg-white overflow-hidden group">
              {/* Quote Icon */}
              <div className="absolute top-4 right-4 opacity-10 group-hover:opacity-20 transition-opacity duration-300">
                <Quote className="h-12 w-12 text-emerald-600" />
              </div>
              
              <CardContent className="p-0 relative z-10">
                {/* Rating */}
                <div className="mb-4">
                  {renderStars(testimonial.rating)}
                </div>

                {/* Review Text */}
                <p className="text-slate-700 mb-6 leading-relaxed font-medium">
                  "{testimonial.text}"
                </p>

                {/* Customer Info */}
                <div className="flex items-center space-x-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100">
                    <User className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">{testimonial.name}</div>
                    <div className="text-sm text-slate-600">{testimonial.company}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))) : (
            <div className="col-span-full text-center py-12">
              <div className="text-slate-500 text-lg">No reviews available at the moment.</div>
            </div>
          )}
        </div>

        {/* View More/Less Button */}
        {testimonials.length > maxReviews && (
          <div className="text-center mb-16">
            <Button
              onClick={() => setShowAll(!showAll)}
              variant="outline"
              className="border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 px-8 py-3 text-lg font-semibold rounded-xl transition-all duration-300"
            >
              {showAll ? `Show Less Reviews` : `View All ${testimonials.length} Reviews`}
            </Button>
          </div>
        )}

        {/* Review Form */}
        <div className="max-w-2xl mx-auto">
          <Card className="p-8 border-0 shadow-xl bg-white">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">
                Share Your Experience
              </h3>
              <p className="text-slate-600">
                Help other businesses discover our reliable delivery service
              </p>
            </div>

            {isSubmitted ? (
              <div className="text-center py-8">
                <div className="flex justify-center mb-4">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
                    <CheckCircle className="h-8 w-8 text-emerald-600" />
                  </div>
                </div>
                <h4 className="text-xl font-semibold text-slate-900 mb-2">
                  Thank You for Your Review!
                </h4>
                <p className="text-slate-600">
                  {submitMessage || 'We appreciate your feedback and will review it shortly.'}
                </p>
              </div>
            ) : (
              <div>
                {submitMessage && !isSubmitted && (
                  <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200">
                    <p className="text-red-700">{submitMessage}</p>
                  </div>
                )}
                <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name & Company */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Your Name *
                    </label>
                    <Input
                      type="text"
                      name="name"
                      value={reviewForm.name}
                      onChange={handleInputChange}
                      placeholder="Enter your name"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Company Name
                    </label>
                    <Input
                      type="text"
                      name="company"
                      value={reviewForm.company}
                      onChange={handleInputChange}
                      placeholder="Your company"
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
                    />
                  </div>
                </div>

                {/* Rating */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">
                    Rating *
                  </label>
                  <div className="flex items-center space-x-2">
                    {renderStars(reviewForm.rating, true, 'h-8 w-8')}
                    <span className="ml-3 text-slate-600 font-medium">
                      {reviewForm.rating} out of 5 stars
                    </span>
                  </div>
                </div>

                {/* Review Text */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Your Review *
                  </label>
                  <Textarea
                    name="text"
                    value={reviewForm.text}
                    onChange={handleInputChange}
                    placeholder="Share your experience with AIS Express delivery service..."
                    rows={4}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200 resize-none"
                  />
                </div>

                {/* Submit Button */}
                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-4 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Submitting...</span>
                    </div>
                  ) : (
                    'Submit Review'
                  )}
                </Button>
              </form>
              </div>
            )}
          </Card>
        </div>
      </div>
    </section>
  );
};